import {FC, useMemo} from 'react'
import Select, {FormatOptionLabelMeta, StylesConfig} from 'react-select'
import {UserCompanyModel} from '../../../auth/core/_models'
import {
  BusinessOption,
  BusinessOptionData,
  businessMatchesSearch,
  businessOptionStyle,
} from './BusinessOption'
import {BusinessSwitcherLabels, businessOptionData, businessOptionText} from './businessSwitcherLabels'

type Option = {value: number; label: string; business: BusinessOptionData}

const formatOptionLabel = (option: Option, {context}: FormatOptionLabelMeta<Option>) => (
  <BusinessOption business={option.business} compact={context === 'value'} />
)

const styles: StylesConfig<Option, false> = {
  control: (base) => ({...base, minHeight: 40}),
  menuPortal: (base) => ({...base, zIndex: 10000}),
  // At most the screen's width less its margins: on a phone the 280px menu ran past the edge and cut a label (508).
  menu: (base) => ({...base, zIndex: 10000, minWidth: 'min(280px, calc(100vw - 32px))'}),
  option: businessOptionStyle,
}

/** Why the server refused to switch the business, in words the user can act on. */
export const readBusinessChangeError = (error: any) => {
  if (error?.response?.status === 403) return 'دسترسی به این کسب‌وکار مجاز نیست.'
  const data = error?.response?.data
  const message = data?.Message || data?.message || (typeof data === 'string' ? data : '')
  return message || 'تغییر کسب‌وکار انجام نشد. دوباره تلاش کنید.'
}

type Props = {
  businesses: UserCompanyModel[]
  labels: BusinessSwitcherLabels
  value?: number
  onChange: (companyId: number) => void
  isLoading?: boolean
  isDisabled?: boolean
  placeholder?: string
  ariaLabel?: string
  inputId?: string
}

/**
 * A searchable combo over the session's own businesses: each one on two lines (name and label, then
 * the taxpayer type, coloured, and the economic code). The search matches the name, the label, the
 * type and the codes, whatever digits or Arabic letters are typed.
 */
const BusinessSelect: FC<Props> = ({
  businesses,
  labels,
  value,
  onChange,
  isLoading = false,
  isDisabled = false,
  placeholder = 'انتخاب کسب‌وکار',
  ariaLabel = 'انتخاب کسب‌وکار',
  inputId,
}) => {
  const options = useMemo<Option[]>(
    () =>
      businesses.map((company) => ({
        value: company.id,
        label: businessOptionText(company, labels[company.id]),
        business: businessOptionData(company, labels[company.id]),
      })),
    [businesses, labels]
  )
  const selected = options.find((option) => option.value === value) || null
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  return (
    <Select<Option, false>
      inputId={inputId}
      aria-label={ariaLabel}
      options={options}
      value={selected}
      onChange={(option) => option && onChange(option.value)}
      filterOption={(option, input) => businessMatchesSearch(option.data.business, input)}
      formatOptionLabel={formatOptionLabel}
      isSearchable
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      noOptionsMessage={() => 'کسب‌وکاری با این نام یا کد پیدا نشد.'}
      loadingMessage={() => 'در حال تغییر کسب‌وکار...'}
      menuPosition='fixed'
      menuPortalTarget={menuPortalTarget}
      styles={styles}
    />
  )
}

export {BusinessSelect}
